import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useTabParam } from '@structural-climbing/ui'
import { useGymContext } from '../contexts/GymContext'
import { AboutStep, EntryStep, WhenStep } from '../features/competitions/setup/FormSteps'
import { StagesBar } from '../features/competitions/setup/StagesBar'
import { StepPanel } from '../features/competitions/setup/StepPanel'
import { StepRail, type RailStep } from '../features/competitions/setup/StepRail'
import { SETUP_STEPS, type SetupStep } from '../features/competitions/setup/steps'
import {
  useCompetitionForm,
  type FormDraft,
} from '../features/competitions/setup/useCompetitionForm'
import { stepLabel } from '../features/competitions/setup/stepLabel'

type NewStep = 'about' | 'when' | 'entry'
const NEW_STEPS = ['about', 'when', 'entry'] as const

/**
 * Nothing reaches the server before step 3, so the fields typed so far live in
 * this browser until then. A refresh, a closed tab or a trip to another page
 * comes back to them; creating the competition, or Cancel, clears them.
 * Per gym, because one person can run two.
 */
const draftKey = (gymId: string) => `competition-draft:${gymId}`

function readDraft(gymId: string): FormDraft | null {
  try {
    const raw = window.localStorage.getItem(draftKey(gymId))
    return raw ? (JSON.parse(raw) as FormDraft) : null
  } catch {
    return null
  }
}

function writeDraft(gymId: string, draft: FormDraft | null) {
  try {
    if (draft) window.localStorage.setItem(draftKey(gymId), JSON.stringify(draft))
    else window.localStorage.removeItem(draftKey(gymId))
  } catch {
    // Storage off or full: the page still works, it just forgets on refresh.
  }
}

/**
 * A new competition, as a full page rather than a modal: the first three of
 * the seven setup steps. Step 3 creates it as a draft and hands over to the
 * competition's own page at step 4.
 */
export function NewCompetitionPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { slug, gymId } = useGymContext()
  const [savedDraft] = useState(() => readDraft(gymId))
  const form = useCompetitionForm(null, savedDraft)
  // In the URL, so a refresh lands back on the same step.
  const [step, setStep] = useTabParam<NewStep>('step', NEW_STEPS, 'about')
  const [created, setCreated] = useState(false)

  const snapshot = JSON.stringify(form.snapshot)
  useEffect(() => {
    // Nothing typed yet is not worth keeping, and must not overwrite a draft
    // before the form has loaded it.
    if (created) return
    const draft = JSON.parse(snapshot) as FormDraft
    if (draft.name.trim() || draft.description.trim() || draft.startsAt) writeDraft(gymId, draft)
  }, [snapshot, gymId, created])

  // Every step is shown so the whole path is visible; the last four wait for
  // the competition to exist.
  const steps: RailStep[] = SETUP_STEPS.map((key, i) => ({
    key,
    label: stepLabel(key, form.format, t),
    done: i < ['about', 'when', 'entry'].indexOf(step),
    isDisabled: i > 2,
  }))

  function go(next: SetupStep) {
    if (next === 'about' || next === 'when' || next === 'entry') setStep(next)
  }

  function advance(from: NewStep, to: NewStep) {
    if (form.validate(from)) return
    setStep(to)
  }

  async function create() {
    const result = await form.save()
    if (!result.ok) {
      if (result.step) setStep(result.step)
      return
    }
    setCreated(true)
    writeDraft(gymId, null)
    navigate(`/${slug}/competitions/${result.id}?step=categories`, { replace: true })
  }

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <Link to={`/${slug}/competitions`} className="text-sm text-primary hover:underline">
            ← {t('competitions.backToList')}
          </Link>
          <h1 className="mt-1 text-2xl font-bold break-words">
            {form.name.trim() || t('competitionSetup.newTitle')}
          </h1>
        </div>
        <StagesBar stage="setup" doneSteps={0} published={false} onChange={() => {}} />
      </div>

      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-6">
        <StepRail
          steps={steps}
          current={step}
          onSelect={go}
          note={t('competitionSetup.draftNote')}
        />

        {step === 'about' && (
          <StepPanel
            onBack={() => {
              setCreated(true)
              writeDraft(gymId, null)
              navigate(`/${slug}/competitions`)
            }}
            backLabel={t('common.cancel')}
            primary={{
              label: t('competitionSetup.continue'),
              onPress: () => advance('about', 'when'),
            }}
            error={form.error}
          >
            <AboutStep form={form} />
          </StepPanel>
        )}
        {step === 'when' && (
          <StepPanel
            onBack={() => setStep('about')}
            primary={{
              label: t('competitionSetup.continue'),
              onPress: () => advance('when', 'entry'),
            }}
            error={form.error}
          >
            <WhenStep form={form} />
          </StepPanel>
        )}
        {step === 'entry' && (
          <StepPanel
            onBack={() => setStep('when')}
            note={t('competitionSetup.draftSaveNote')}
            primary={{
              label: t('competitionSetup.createAndContinue'),
              onPress: () => void create(),
              isLoading: form.saving,
            }}
            error={form.error}
          >
            <EntryStep form={form} />
          </StepPanel>
        )}
      </div>
    </div>
  )
}
